import React, { useRef, useState, useEffect } from 'react';
import * as Yup from 'yup';
import { ScopeCertificateDates } from '../../../sc-models/scope-certificate-details-dto';
import FormBuilder from '../../../../../shared/components/form-builder/form-builder';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { dtrackItService } from '../../../../transaction-certificates/tc-service/dtrackit.service';
import useNotification from '../../../../../shared/hooks/useNotification';

interface ScopeCertificateDatesFormProps {
  formRef: any;
  datesData: ScopeCertificateDates;
  setDatesData: React.Dispatch<React.SetStateAction<ScopeCertificateDates>>;
}

const ScopeCertificateDatesForm: React.FC<ScopeCertificateDatesFormProps> = ({
  formRef,
  datesData,
  setDatesData,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const { notify } = useNotification();
  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setDatesData(datesData);
  };

  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (countryOptions.length === 0) return;
      
      if (datesData.scPlaceOfIssueCountry && datesData.scPlaceOfIssueState) {
        setSelectedCountry(datesData.scPlaceOfIssueCountry);
        setSelectedState(datesData.scPlaceOfIssueState);
        await getStates(datesData.scPlaceOfIssueCountry);
      } else if (datesData.scPlaceOfIssue) {
        await splitPlaceOfIssue(datesData.scPlaceOfIssue);
      }
    };
    
    loadData();
  }, [datesData.scPlaceOfIssue, countryOptions]);

  // Fetch countries
  const getCountries = async () => {
    try {
      const result = await dtrackItService.getCountry();
      const countries = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setCountryOptions(countries);
    } catch (err) {
      notify('Failed to fetch countries', 'error');
    }
  };

  // Fetch states based on selected country
  const getStates = async (countryCode: string) => {
    try {
      const countryParam = JSON.stringify(`[${[countryCode].map((item) => `'${item}'`)}]`).replace(/^"|"$/g, '');
      const result = await dtrackItService.getStateBasedonCountry(countryParam);
      const states = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
        description: t.description_1,
      }));
      setStateOptions(states);
    } catch (err) {
      setStateOptions([]);
      notify('Failed to fetch states', 'error');
    }
  };

  // Split Place of Issue into State and Country
  const splitPlaceOfIssue = async (placeOfIssue: string) => {
    if (!placeOfIssue) return;
    
    const parts = placeOfIssue.split(',').map(part => part.trim());
    
    if (parts.length === 2) {
      const [stateDescription, countryIdentifier] = parts;
      
      let matchingCountry = countryOptions.find(c => c.value === countryIdentifier);
      if (!matchingCountry) {
        matchingCountry = countryOptions.find(c => 
          c.label.toLowerCase().startsWith(countryIdentifier.toLowerCase())
        );
      }
      
      const countryCode = matchingCountry?.value || countryIdentifier;
      setSelectedCountry(countryCode);
      
      if (countryCode) {
        try {
          const countryParam = JSON.stringify(`[${[countryCode].map((item) => `'${item}'`)}]`).replace(/^"|"$/g, '');
          const result = await dtrackItService.getStateBasedonCountry(countryParam);
          const states = result.data.map((t: any) => ({
            value: t.code_1,
            label: `${t.description_1} (${t.code_1})`,
            description: t.description_1,
          }));
          setStateOptions(states);
          
          const matchingState = states.find((s: any) => 
            s.description.toLowerCase() === stateDescription.toLowerCase()
          );
          
          if (matchingState) {
            setSelectedState(matchingState.value);
            formRef.current?.setFieldValue('scPlaceOfIssueCountry', countryCode);
            formRef.current?.setFieldValue('scPlaceOfIssueState', matchingState.value);

            const countryName = matchingCountry?.label?.split(' (')[0] || countryCode;
            formRef.current?.setFieldValue('scPlaceOfIssue', `${matchingState.description}, ${countryName}`);
          } else {
            setSelectedState(stateDescription);
            formRef.current?.setFieldValue('scPlaceOfIssueCountry', countryCode);
            formRef.current?.setFieldValue('scPlaceOfIssueState', stateDescription);

            const countryName = matchingCountry?.label?.split(' (')[0] || countryCode;
            formRef.current?.setFieldValue('scPlaceOfIssue', `${stateDescription}, ${countryName}`);
          }
        } catch (err) {
          setStateOptions([]);
          notify('Failed to load state information', 'error');
        }
      }
    }
  };

  const combinePlaceOfIssue = (state: string, country: string): string => {
    if (state && country) {
      const selectedCountryObj = countryOptions.find(c => c.value === country);
      const countryName = selectedCountryObj?.label?.split(' (')[0] || country;
      return `${state}, ${countryName}`;
    }
    return '';
  };

  const formConfig = {
    pageName: "scope-certificate-details",
    initialValues: datesData,
    validationSchema: Yup.object().shape({
      scLastAuditStart: Yup.date()
        .nullable(),
      
      scLastAuditDate: Yup.date()
        .required('Last Audit Date is required')
        .test('audit-date-sequence', 'Last Audit Date must be after or equal to Last Audit Start Date', 
          function(value) {
            const startDate = this.parent.scLastAuditStart;
            if (!startDate || !value) return true; // Skip validation if either date is null
            return new Date(startDate) <= new Date(value);
          }),
      
      scDateOfIssue: Yup.date()
        .required('Date of Issue is required')
        .test('issue-date-sequence', 'Date of Issue must be after or equal to Last Audit Date',
          function(value) {
            const auditDate = this.parent.scLastAuditDate;
            if (!auditDate || !value) return true;
            return new Date(auditDate) <= new Date(value);
          }),
      
      scLastUpdated: Yup.date()
        .required('Last Updated date is required')
        .test('last-updated-sequence', 'Last Updated date must be after or equal to Date of Issue',
          function(value) {
            const issueDate = this.parent.scDateOfIssue;
            if (!issueDate || !value) return true;
            return new Date(issueDate) <= new Date(value);
          }),
      
      scValidUntil: Yup.date()
        .required('Valid Until date is required')
        .test('valid-until-sequence', 'Valid Until date must be after or equal to Last Updated date',
          function(value) {
            const lastUpdated = this.parent.scLastUpdated;
            if (!lastUpdated || !value) return true;
            return new Date(lastUpdated) <= new Date(value);
          }),
      
      scExtendedUntil: Yup.date()
        .nullable()
        .test('extended-until-sequence', 'Extended Until date must be after or equal to Valid Until date',
          function(value) {
            const validUntil = this.parent.scValidUntil;
            if (!validUntil || !value) return true;
            return new Date(validUntil) <= new Date(value);
          }),
      
      scPlaceOfIssueCountry: Yup.string()
        .required('Country is required'),
      
      scPlaceOfIssueState: Yup.string()
        .required('State is required')
        .test('not-invalid-value', 'n/a, N/A, NULL, null, Not available are not accepted', 
          function(value) {
            if (!value) return true; // Let required handle empty values
            const trimmedValue = value.trim().toLowerCase();
            const invalidValues = ['n/a', 'null', 'not available'];
            return !invalidValues.includes(trimmedValue);
          }),
      
      scPlaceOfIssue: Yup.string()
        .required('SC place of issue is required'),
      scTransferAuditDate: Yup.date().nullable(),
    }),
    readOnly: hasSubmitSC,
    sections: [
      {
        title: 'Scope Certificate Dates',
        fields: [
          {
            label: 'SC Last Audit Start Date',
            name: 'scLastAuditStart',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: { placeholder: 'Select Last Audit Start date' },
          },
          {
            label: 'SC Last Audit Date',
            name: 'scLastAuditDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: { placeholder: 'Select Last Audit Date' },
          },
          {
            label: 'SC Date of Issue',
            name: 'scDateOfIssue',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: { placeholder: 'Select Date of Issue', maxDate: moment().add(100, "years") },
          },
          {
            label: 'SC Last Updated',
            name: 'scLastUpdated',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: { placeholder: 'Select Last Updated date', maxDate: moment().add(100, "years") },
          },
          {
            label: 'SC Valid Until',
            name: 'scValidUntil',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: {
              placeholder: 'Select Valid Until date',
              maxDate: moment().add(100, "years")
            },
          },
          {
            label: 'SC Extended Until',
            name: 'scExtendedUntil',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: { placeholder: 'Select Extended Until date',
              maxDate: moment().add(100, "years")
             },
          },
          {
            label: 'SC Place of Issue Country',
            name: 'scPlaceOfIssueCountry',
            type: 'select',
            required: true,
            gridSize: { xs: 12, md: 6, lg: 3 },
            events: {
              onChange: (newValue: any) => {
                setSelectedCountry(newValue);
                setSelectedState('');
                formRef.current?.setFieldValue('scPlaceOfIssueState', '');
                formRef.current?.setFieldValue('scPlaceOfIssue', '');
                
                if (newValue) {
                  getStates(newValue);
                }
              },
            },
            props: {
              enableSearch: true,
              options: countryOptions,
              placeholder: 'Select Country',
              fullWidth: true,
            },
          },
          {
            label: 'SC Place of Issue State',
            name: 'scPlaceOfIssueState',
            type: 'select',
            required: true,
            gridSize: { xs: 12, md: 6, lg: 3 },
            disabled: (values: any) => !values.scPlaceOfIssueCountry,
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                setSelectedState(newValue);
                const selectedStateObj = stateOptions.find(s => s.value === newValue);
                const stateDescription = selectedStateObj?.description || newValue;
                const combinedValue = combinePlaceOfIssue(stateDescription, updatedValues.scPlaceOfIssueCountry);
                formRef.current?.setFieldValue('scPlaceOfIssue', combinedValue);
              },
            },
            props: {
              enableSearch: true,
              options: stateOptions,
              placeholder: 'Select State',
              fullWidth: true,
            },
          },
          {
            label: 'SC Place of Issue',
            name: 'scPlaceOfIssue',
            type: 'text',
            required: true,
            gridSize: { xs: 12, md: 6, lg: 3 },
            props: { 
              placeholder: 'Auto-filled from Country and State',
              disabled: true,
            },
          },
          {
            label: 'SC Transfer Audit Date',
            name: 'scTransferAuditDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: { placeholder: 'Select Transfer Audit Date' },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      // Ensure scPlaceOfIssue is properly set from country and state
      const finalValues = {
        ...values,
        scPlaceOfIssue: values.scPlaceOfIssue || combinePlaceOfIssue(values.scPlaceOfIssueState, values.scPlaceOfIssueCountry),
      };
      
      setDatesData(finalValues);
      toggleDrawer();
    },
  };

  return <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />;
};

export default ScopeCertificateDatesForm;

